import { DateFormat } from '@gonfalon/format/constants';
import { formatInTimeZone } from 'date-fns-tz';

import { MS_PER_DAY } from '../../constants';
import { AggregationType, Granularity, type MonthOption, TimeRangeOption } from '../../types';

type MonthOptionsProps = {
  count?: number;
  today: Date;
};

export function getLastNMonthOptionsUTC({ count = 24, today }: MonthOptionsProps): MonthOption[] {
  const baseDate = today;
  const baseYear = baseDate.getUTCFullYear();
  const baseMonth = baseDate.getUTCMonth();

  return Array.from({ length: count }, (_, i) => {
    const offsetMonth = baseMonth - i;
    const date = new Date(Date.UTC(baseYear, offsetMonth, 1)); // always start-of-month UTC

    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const key = `${year}-${month}`;
    const label = formatInTimeZone(date, 'UTC', DateFormat.MMM_YY);

    return { key, label };
  });
}

export function getStartOfDayUTC(date: Date): number {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

type TimestampsProps = {
  timeRange?: TimeRangeOption;
  month?: string;
  today: Date;
};

export function getTimestampsUTC({ timeRange, today, month }: TimestampsProps) {
  switch (timeRange) {
    case TimeRangeOption.MONTH_TO_DATE:
    case undefined:
      if (!month) {
        throw new Error('Month is required for MONTH_TO_DATE time range');
      }
      return getMonthTimestampsUTC(month);

    case TimeRangeOption.THREE_DAY:
      return getRelativeTimeRangeUTC(today, 3, 'days');

    case TimeRangeOption.THIRTY_DAY:
      return getRelativeTimeRangeUTC(today, 30, 'days');

    case TimeRangeOption.THREE_MONTH:
      return getRelativeTimeRangeUTC(today, 3, 'months');

    default:
      throw new Error(`Invalid time range: ${timeRange}`);
  }
}

/**
 * Returns a UTC time range `{ from, to }` spanning the previous number of days or months to the date.
 * - `from` is inclusive
 * - `to` is exclusive
 * - `count = 1` includes the current month or day
 */
export function getRelativeTimeRangeUTC(date: Date, count: number, unit: 'days' | 'months') {
  if (count <= 0) {
    throw new Error('Count must be greater than 0');
  }

  const to = getStartOfDayUTC(date) + MS_PER_DAY;

  if (unit === 'months') {
    const from = Date.UTC(date.getUTCFullYear(), date.getUTCMonth() - (count - 1), 1);
    return { from, to };
  }

  const days = Math.max(0, count) * MS_PER_DAY;
  return { from: to - days, to };
}

/**
 * Converts a month key in the format "YYYY-MM" to a string UTC timestamp range in milliseconds.
 * - `from` is the start of the month (inclusive)
 * - `to` is the start of the next month (exclusive)
 */
export function getMonthTimestampsUTC(key: string) {
  const [year, month] = key.split('-').map(Number); // "2025-03" → 2025, 3

  const startOfMonth = Date.UTC(year, month - 1, 1);
  const startOfNextMonth = Date.UTC(year, month, 1);

  return { from: startOfMonth, to: startOfNextMonth };
}

/**
 * For AVERAGE aggregation, caps `to` at the start of the next hour so the backend
 * never aggregates future (empty) hourly buckets into daily/monthly values.
 * For all other aggregation types, returns `to` unchanged.
 */
export function getEffectiveTo(to: number, today: Date, aggregationType: AggregationType): number {
  if (aggregationType === AggregationType.AVERAGE) {
    return Math.min(to, getCutoffTimestampUTC(today, Granularity.HOURLY));
  }
  return to;
}

/**
 * Returns a UTC timestamp for the cutoff date.
 * - If granularity is hourly, returns the start of the next hour.
 * - Otherwise, returns the start of the next day.
 */
export function getCutoffTimestampUTC(today: Date, granularity?: Granularity): number {
  const baseDate = today;
  const year = baseDate.getUTCFullYear();
  const month = baseDate.getUTCMonth();
  const day = baseDate.getUTCDate();
  const hour = baseDate.getUTCHours();

  return granularity === Granularity.HOURLY
    ? Date.UTC(year, month, day, hour + 1, 0, 0, 0)
    : Date.UTC(year, month, day + 1);
}

/**
 * Returns a range from the previous period to compare against the current month.
 *
 * - The `from` timestamp is the start of the previous month (inclusive).
 * - The `to` timestamp is exclusive and represents the same day last month as `today`,
 *   or the end of the previous month - whichever comes first.
 */
export function getPreviousTimestampsUTC(currentFrom: number, today: Date) {
  const currentDate = new Date(currentFrom);
  const year = currentDate.getUTCFullYear();
  const month = currentDate.getUTCMonth();

  const from = Date.UTC(year, month - 1, 1); // Start of previous month
  const startOfCurrentMonth = Date.UTC(year, month, 1);

  // Exclusive cuttoff date
  const cutoff = getSameDayLastMonthUTC(today) + MS_PER_DAY;
  const to = Math.min(cutoff, startOfCurrentMonth);

  return { from, to };
}

export function getSameDayLastMonthUTC(today: Date): number {
  const currentDay = today.getUTCDate();
  const year = today.getUTCFullYear();
  const lastMonth = today.getUTCMonth() - 1;

  const daysInLastMonth = new Date(Date.UTC(year, lastMonth + 1, 0)).getUTCDate();
  const cutoffDay = Math.min(currentDay, daysInLastMonth);

  return Date.UTC(year, lastMonth, cutoffDay);
}

/**
 * Formats a date in UTC to a string in the format "MMM D, YYYY" or "MMM D" if the year is the current year.
 */
export function formatDateUTC(date: Date) {
  const currentYear = new Date().getUTCFullYear();
  const endYear = date.getUTCFullYear();
  const format = endYear !== currentYear ? DateFormat.MMM_D_YYYY : DateFormat.MMM_DST;
  return formatInTimeZone(date, 'UTC', format);
}
